import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/apiClient';
import Breadcrumb from '../ui/Breadcrumb';
import Modal from '../ui/Modal';
import Spinner from '../ui/Spinner';
import DbSteps from '../ui/DbSteps';
import { moduleColor } from '../ui/modulePalette';
import { IconAlertTriangle, IconCheck, IconGlobe, IconImage, IconPlus, IconTable, IconTrash } from '../ui/icons';

function useApiBaseUrl() {
  const raw = process.env.REACT_APP_API_BASE_URL;
  if (raw && raw.trim()) return raw.replace(/\/+$/, '');
  return '/api';
}

function mediaInlineUrl(apiBaseUrl, siteId, id) {
  const base = String(apiBaseUrl || '/api').replace(/\/+$/, '');
  const qs = new URLSearchParams({
    site_id: String(Number(siteId)),
    id: String(Number(id)),
  });
  return `${base}/media.php?${qs.toString()}`;
}

function newId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`;
}

function defaultMenuData() {
  return {
    version: 1,
    permanent: { categories: [] },
    formulas: [],
  };
}

function cloneMenuData(value) {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return defaultMenuData();
  }
}

function safeMenuData(raw) {
  const base = defaultMenuData();
  const src = raw && typeof raw === 'object' ? raw : {};
  const categories = Array.isArray(src?.permanent?.categories) ? src.permanent.categories : [];
  const formulas = Array.isArray(src?.formulas) ? src.formulas : [];
  return {
    ...base,
    ...src,
    permanent: {
      categories: categories.map((c) => ({
        id: String(c?.id || '') || newId('mc'),
        name: String(c?.name || ''),
        description: String(c?.description || ''),
        items: (Array.isArray(c?.items) ? c.items : []).map((it) => ({
          id: String(it?.id || '') || newId('mi'),
          title: String(it?.title || ''),
          description: String(it?.description || ''),
          price: String(it?.price || ''),
          media_id: it?.media_id == null ? null : Number(it.media_id) || null,
          media_name: String(it?.media_name || ''),
          image_url: String(it?.image_url || ''),
          is_available: it?.is_available !== false,
        })),
      })),
    },
    formulas: formulas.map((f) => ({
      id: String(f?.id || '') || newId('mf'),
      title: String(f?.title || ''),
      subtitle: String(f?.subtitle || ''),
      description: String(f?.description || ''),
      price: String(f?.price || ''),
      media_id: f?.media_id == null ? null : Number(f.media_id) || null,
      media_name: String(f?.media_name || ''),
      image_url: String(f?.image_url || ''),
      is_available: f?.is_available !== false,
    })),
  };
}

function menuFingerprint(menu) {
  try {
    return JSON.stringify(menu || defaultMenuData());
  } catch {
    return '';
  }
}

function imageLabel(item) {
  const name = String(item?.media_name || '').trim();
  if (name) return name;
  return String(item?.image_url || '').trim();
}

function hasText(v) {
  return String(v || '').trim().length > 0;
}

function itemHasContent(item) {
  return hasText(item?.title) || hasText(item?.description) || hasText(item?.price) || hasText(item?.image_url) || Number(item?.media_id) > 0;
}

function categoryHasContent(category) {
  if (hasText(category?.name) || hasText(category?.description)) return true;
  const items = Array.isArray(category?.items) ? category.items : [];
  return items.some((it) => itemHasContent(it));
}

export default function MenusPage({ siteId, site }) {
  const apiBaseUrl = useApiBaseUrl();
  const uploadInputRef = useRef(null);

  const [state, setState] = useState({
    status: 'loading',
    dbConfig: null,
    ensureOk: false,
    error: '',
    menu: defaultMenuData(),
    updatedAt: null,
  });
  const [savedFingerprint, setSavedFingerprint] = useState('');
  const [tab, setTab] = useState('permanent');
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [siteMedia, setSiteMedia] = useState({ status: 'idle', error: '', assets: [] });

  const accent = moduleColor('menus');

  const refresh = useCallback(() => {
    if (!siteId) return;
    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .dbConnections(Number(siteId))
      .then(async (dbRes) => {
        if (cancelled) return;
        if (!dbRes.ok) {
          setState((s) => ({
            ...s,
            status: 'error',
            dbConfig: null,
            ensureOk: false,
            error: dbRes.error || 'Impossible de charger la config BDD',
          }));
          return;
        }
        const dbConfig = (dbRes.connections || [])[0] || null;
        setState((s) => ({ ...s, status: 'ready', dbConfig, error: '' }));

        if (!dbConfig) {
          setState((s) => ({
            ...s,
            dbConfig: null,
            ensureOk: false,
            error: '',
          }));
          return;
        }

        const res = await api.menusGet(Number(siteId));
        if (cancelled) return;
        if (!res.ok) {
          setState((s) => ({
            ...s,
            ensureOk: false,
            error: res.status === 409 ? res.error : res.error || 'Chargement impossible',
          }));
          return;
        }
        const safe = safeMenuData(res.menu);
        setState((s) => ({
          ...s,
          status: 'ready',
          ensureOk: true,
          error: '',
          menu: safe,
          updatedAt: res.updated_at || null,
        }));
        setSavedFingerprint(menuFingerprint(safe));
      })
      .catch(() => {
        if (cancelled) return;
        setState((s) => ({ ...s, status: 'error', dbConfig: null, ensureOk: false, error: 'Chargement impossible' }));
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => refresh(), [refresh]);

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setSiteMedia((s) => ({ ...s, status: 'loading', error: '' }));
    api
      .mediaList(Number(siteId))
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setSiteMedia({ status: 'error', error: res.error || 'Chargement médias impossible', assets: [] });
          return;
        }
        setSiteMedia({ status: 'ready', error: '', assets: res.assets || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setSiteMedia({ status: 'error', error: 'Chargement médias impossible', assets: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  const categories = useMemo(() => {
    return Array.isArray(state.menu?.permanent?.categories) ? state.menu.permanent.categories : [];
  }, [state.menu]);

  const formulas = useMemo(() => {
    return Array.isArray(state.menu?.formulas) ? state.menu.formulas : [];
  }, [state.menu]);

  const imageAssets = useMemo(() => {
    return (siteMedia.assets || []).filter((a) => a && !a.is_hidden && String(a.kind || '') === 'image');
  }, [siteMedia.assets]);

  const dbLoading = state.status === 'loading';
  const dbOk = !dbLoading && Boolean(state.dbConfig && state.ensureOk);
  const dbNeedsAttention = !dbLoading && Boolean(!state.dbConfig || (state.dbConfig && !state.ensureOk));
  const dbLabel = dbLoading ? 'Chargement…' : !state.dbConfig ? 'Connexion manquante' : state.ensureOk ? 'OK' : 'Tables manquantes';

  const isDirty = useMemo(() => {
    return Boolean(savedFingerprint && menuFingerprint(state.menu) !== savedFingerprint);
  }, [state.menu, savedFingerprint]);

  function patchMenu(updater) {
    setState((prev) => {
      const current = safeMenuData(prev.menu);
      const draft = cloneMenuData(current);
      updater?.(draft);
      return { ...prev, menu: safeMenuData(draft) };
    });
  }

  function addCategory() {
    patchMenu((m) => {
      const cats = Array.isArray(m?.permanent?.categories) ? m.permanent.categories : [];
      cats.push({ id: newId('mc'), name: '', description: '', items: [] });
      m.permanent = { categories: cats };
    });
  }

  function removeCategory(categoryId) {
    patchMenu((m) => {
      m.permanent.categories = (m?.permanent?.categories || []).filter((c) => String(c.id) !== String(categoryId));
    });
  }

  function patchCategory(categoryId, patch) {
    patchMenu((m) => {
      m.permanent.categories = (m?.permanent?.categories || []).map((c) => (String(c.id) === String(categoryId) ? { ...c, ...(patch || {}) } : c));
    });
  }

  function addItem(categoryId) {
    patchMenu((m) => {
      m.permanent.categories = (m?.permanent?.categories || []).map((c) => {
        if (String(c.id) !== String(categoryId)) return c;
        const items = Array.isArray(c.items) ? c.items : [];
        return {
          ...c,
          items: [
            ...items,
            {
              id: newId('mi'),
              title: '',
              description: '',
              price: '',
              media_id: null,
              media_name: '',
              image_url: '',
              is_available: true,
            },
          ],
        };
      });
    });
  }

  function patchItem(categoryId, itemId, patch) {
    patchMenu((m) => {
      m.permanent.categories = (m?.permanent?.categories || []).map((c) => {
        if (String(c.id) !== String(categoryId)) return c;
        return {
          ...c,
          items: (c.items || []).map((it) => (String(it.id) === String(itemId) ? { ...it, ...(patch || {}) } : it)),
        };
      });
    });
  }

  function removeItem(categoryId, itemId) {
    patchMenu((m) => {
      m.permanent.categories = (m?.permanent?.categories || []).map((c) => {
        if (String(c.id) !== String(categoryId)) return c;
        return {
          ...c,
          items: (c.items || []).filter((it) => String(it.id) !== String(itemId)),
        };
      });
    });
  }

  function patchFormula(formulaId, patch) {
    patchMenu((m) => {
      m.formulas = (m.formulas || []).map((f) => (String(f.id) === String(formulaId) ? { ...f, ...(patch || {}) } : f));
    });
  }

  function removeFormula(formulaId) {
    patchMenu((m) => {
      m.formulas = (m.formulas || []).filter((f) => String(f.id) !== String(formulaId));
    });
  }

  function requestRemoveCategory(category) {
    const c = category || null;
    if (!c) return;
    if (!categoryHasContent(c)) {
      removeCategory(c.id);
      return;
    }
    setModal({
      type: 'confirm-delete',
      kind: 'category',
      title: String(c.name || '').trim() || 'Catégorie',
      onConfirm: () => removeCategory(c.id),
    });
  }

  function requestRemoveItem(categoryId, item) {
    const it = item || null;
    if (!it) return;
    if (!itemHasContent(it)) {
      removeItem(categoryId, it.id);
      return;
    }
    setModal({
      type: 'confirm-delete',
      kind: 'item',
      title: String(it.title || '').trim() || 'Élément',
      onConfirm: () => removeItem(categoryId, it.id),
    });
  }

  function requestRemoveFormula(formula) {
    const f = formula || null;
    if (!f) return;
    if (!itemHasContent(f) && !hasText(f.subtitle)) {
      removeFormula(f.id);
      return;
    }
    setModal({
      type: 'confirm-delete',
      kind: 'formula',
      title: String(f.title || '').trim() || 'Formule',
      onConfirm: () => removeFormula(f.id),
    });
  }

  function itemImageSrc(item) {
    const mid = Number(item?.media_id || 0);
    if (Number.isFinite(mid) && mid > 0) return mediaInlineUrl(apiBaseUrl, Number(siteId), mid);
    return String(item?.image_url || '').trim();
  }

  function openImagePicker(target, refs = {}) {
    setModal({
      type: 'image-picker',
      target: String(target || ''),
      categoryId: refs.categoryId ? String(refs.categoryId) : '',
      itemId: refs.itemId ? String(refs.itemId) : '',
      formulaId: refs.formulaId ? String(refs.formulaId) : '',
      customUrl: '',
      uploading: false,
      error: '',
    });
  }

  function openNewFormulaModal() {
    setModal({
      type: 'new-formula',
      title: '',
      subtitle: '',
      description: '',
      price: '',
      saving: false,
      error: '',
    });
  }

  function createFormulaFromModal() {
    if (!modal || modal.type !== 'new-formula') return;
    if (modal.saving) return;
    const next = {
      id: newId('mf'),
      title: String(modal.title || ''),
      subtitle: String(modal.subtitle || ''),
      description: String(modal.description || ''),
      price: String(modal.price || ''),
      media_id: null,
      media_name: '',
      image_url: '',
      is_available: true,
    };
    patchMenu((m) => {
      const list = Array.isArray(m.formulas) ? m.formulas : [];
      m.formulas = [next, ...list];
    });
    setModal(null);
  }

  function applyMediaToPickerTarget(asset) {
    const a = asset && typeof asset === 'object' ? asset : null;
    if (!a || !modal || modal.type !== 'image-picker') return;
    const patch = {
      media_id: Number(a.id),
      media_name: String(a.original_name || a.note || `Media #${a.id}`),
      image_url: '',
    };
    if (modal.target === 'item') {
      patchItem(modal.categoryId, modal.itemId, patch);
    } else if (modal.target === 'formula') {
      patchFormula(modal.formulaId, patch);
    }
    setModal(null);
  }

  function applyUrlToPickerTarget() {
    if (!modal || modal.type !== 'image-picker') return;
    const nextUrl = String(modal.customUrl || '').trim();
    if (!nextUrl) return;
    const patch = { media_id: null, media_name: '', image_url: nextUrl };
    if (modal.target === 'item') {
      patchItem(modal.categoryId, modal.itemId, patch);
    } else if (modal.target === 'formula') {
      patchFormula(modal.formulaId, patch);
    }
    setModal(null);
  }

  async function uploadFromPicker(files) {
    const list = Array.from(files || []);
    if (!siteId || !list.length || !modal || modal.type !== 'image-picker') return;
    setModal((m) => (m?.type === 'image-picker' ? { ...m, uploading: true, error: '' } : m));
    const res = await api.mediaUpload(Number(siteId), list);
    if (!res.ok) {
      setModal((m) => (m?.type === 'image-picker' ? { ...m, uploading: false, error: res.error || 'Upload impossible' } : m));
      return;
    }

    const allAssets = Array.isArray(res.assets) ? res.assets : [];
    setSiteMedia({ status: 'ready', error: '', assets: allAssets });

    const names = new Set(list.map((f) => String(f.name || '').trim()).filter(Boolean));
    const candidate = allAssets
      .filter((a) => a && String(a.kind || '') === 'image')
      .find((a) => names.has(String(a.original_name || '').trim()));

    if (candidate) {
      applyMediaToPickerTarget(candidate);
      return;
    }

    setModal((m) => (m?.type === 'image-picker' ? { ...m, uploading: false } : m));
  }

  async function saveMenu() {
    if (!siteId || saving || !state.ensureOk) return;
    setSaving(true);
    setState((s) => ({ ...s, error: '' }));
    const res = await api.menusSave({ site_id: Number(siteId), menu: state.menu });
    setSaving(false);
    if (!res.ok) {
      setState((s) => ({ ...s, error: res.error || 'Enregistrement impossible' }));
      return;
    }
    const safe = safeMenuData(res.menu || state.menu);
    setState((s) => ({ ...s, menu: safe, updatedAt: res.updated_at || s.updatedAt }));
    setSavedFingerprint(menuFingerprint(safe));
  }

  async function ensureTables() {
    const res = await api.menusEnsure(Number(siteId));
    if (!res.ok) return res;
    refresh();
    return res;
  }

  if (!siteId) {
    return (
      <div className="Page">
        <div className="Card">
          <div className="TileTitle">Menus</div>
          <div className="Muted">Sélectionne un site.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Page Page--menus">
      <header className="PageHeader">
        <div className="PageHeaderBar">
          <Breadcrumb
            items={[{ label: 'Menus' }]}
            badges={
              <span className="Badge" style={{ borderColor: accent, color: accent }}>
                {site?.name || 'Site'}
              </span>
            }
          />
          <div className="Badges MenusHeaderActions">
            <button
              type="button"
              className={`Button Button--ghost ${dbOk ? 'Button--good' : dbNeedsAttention ? 'Button--danger' : ''} ${dbLoading ? 'Button--loading' : ''}`}
              disabled={false}
              onClick={() => setModal({ type: 'db-status' })}
              title={`BDD Menus — ${dbLabel}`}
            >
              <span className="ButtonInline">
                {dbLoading ? <Spinner className="Spinner--sm" /> : dbOk ? <IconCheck /> : dbNeedsAttention ? <IconAlertTriangle /> : <IconTable />} BDD Menus
              </span>
            </button>
            <button type="button" className="Button" disabled={saving || state.status === 'loading' || !state.ensureOk} onClick={saveMenu}>
              <span className="ButtonInline">{saving ? <Spinner className="Spinner--sm" /> : <IconCheck />} Enregistrer</span>
            </button>
          </div>
        </div>
      </header>

      {state.error ? (
        <div className="Error" role="alert" style={{ marginBottom: 12 }}>
          {state.error}
        </div>
      ) : null}

      {!state.dbConfig ? <div className="Hint">Connexion manquante: clique sur “BDD Menus”.</div> : null}
      {state.dbConfig && !state.ensureOk ? <div className="Hint">Tables Menus non initialisées: clique sur “BDD Menus”.</div> : null}

      {state.status === 'loading' ? (
        <div className="Card" role="status" aria-live="polite">
          Chargement du menu…
        </div>
      ) : null}

      {state.ensureOk ? (
        <>
          <div className="MenusTabs" role="tablist" aria-label="Édition du menu">
            <button
              type="button"
              className={`MenusTab ${tab === 'permanent' ? 'is-active' : ''}`}
              onClick={() => setTab('permanent')}
              role="tab"
              aria-selected={tab === 'permanent' ? 'true' : 'false'}
            >
              Carte permanente
            </button>
            <button
              type="button"
              className={`MenusTab ${tab === 'formulas' ? 'is-active' : ''}`}
              onClick={() => setTab('formulas')}
              role="tab"
              aria-selected={tab === 'formulas' ? 'true' : 'false'}
            >
              Formules
            </button>
          </div>

          {state.status !== 'loading' && tab === 'permanent' ? (
            <div className="MenusSection">
              <div className="MenusSectionHead">
                <div className="TileTitle">Carte permanente</div>
                <button type="button" className="Button Button--ghost" onClick={addCategory}>
                  <span className="ButtonInline">
                    <IconPlus /> Catégorie
                  </span>
                </button>
              </div>

              {categories.length === 0 ? <div className="Muted">Aucune catégorie. Ajoute une catégorie pour commencer.</div> : null}

              <div className="MenusStack">
                {categories.map((cat) => (
                  <article key={cat.id} className="MenusCategory">
                    <div className="MenusCardHead MenusCategoryHead">
                      <input
                        className="Input MenusTitleInput MenusCategoryTitle"
                        value={String(cat.name || '')}
                        onChange={(e) => patchCategory(cat.id, { name: e.target.value })}
                        placeholder="Nom de la catégorie (Entrées, Plats...)"
                      />
                      <button type="button" className="Button Button--ghost Button--danger" onClick={() => requestRemoveCategory(cat)}>
                        <IconTrash />
                      </button>
                    </div>
                    <textarea
                      className="Textarea"
                      rows={2}
                      value={String(cat.description || '')}
                      onChange={(e) => patchCategory(cat.id, { description: e.target.value })}
                      placeholder="Description (optionnel)"
                    />

                    <div className="MenusItems">
                      {(cat.items || []).map((it) => {
                        const src = itemImageSrc(it);
                        return (
                          <div key={it.id} className="MenusItemCard">
                            <div className="MenusItemLayout">
                              <button type="button" className="MenusImageSquare" onClick={() => openImagePicker('item', { categoryId: cat.id, itemId: it.id })}>
                                {src ? <img src={src} alt="" loading="lazy" /> : <IconImage />}
                              </button>
                              <div>
                                <div className="MenusItemTop">
                                  <input
                                    className="Input"
                                    value={String(it.title || '')}
                                    onChange={(e) => patchItem(cat.id, it.id, { title: e.target.value })}
                                    placeholder="Titre du plat"
                                  />
                                  <input
                                    className="Input MenusPriceInput"
                                    value={String(it.price || '')}
                                    onChange={(e) => patchItem(cat.id, it.id, { price: e.target.value })}
                                    placeholder="Prix"
                                  />
                                </div>
                                <textarea
                                  className="Textarea"
                                  rows={2}
                                  value={String(it.description || '')}
                                  onChange={(e) => patchItem(cat.id, it.id, { description: e.target.value })}
                                  placeholder="Description"
                                />
                                <div className="Muted OneLine" style={{ marginTop: 6 }}>
                                  {imageLabel(it) || 'Aucune image'}
                                </div>
                              </div>
                            </div>

                            <div className="MenusItemActions">
                              <label className="ButtonInline MenusToggleLine">
                                <input
                                  type="checkbox"
                                  checked={Boolean(it.is_available !== false)}
                                  onChange={(e) => patchItem(cat.id, it.id, { is_available: Boolean(e.target.checked) })}
                                />
                                Disponible
                              </label>
                              <button type="button" className="Button Button--ghost Button--danger" onClick={() => requestRemoveItem(cat.id, it)}>
                                <IconTrash />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <button type="button" className="Button Button--ghost" onClick={() => addItem(cat.id)}>
                      <span className="ButtonInline">
                        <IconPlus /> Plat
                      </span>
                    </button>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          {state.status !== 'loading' && tab === 'formulas' ? (
            <div className="MenusSection">
              <div className="MenusSectionHead">
                <div className="TileTitle">Formules</div>
                <button type="button" className="Button Button--ghost" onClick={openNewFormulaModal}>
                  <span className="ButtonInline">
                    <IconPlus /> Nouvelle formule
                  </span>
                </button>
              </div>

              {formulas.length === 0 ? <div className="Muted">Aucune formule pour le moment.</div> : null}

              <div className="MenusStack">
                {formulas.map((f) => {
                  const src = itemImageSrc(f);
                  return (
                    <article key={f.id} className="Card MenusCard">
                      <div className="MenusItemLayout">
                        <button type="button" className="MenusImageSquare" onClick={() => openImagePicker('formula', { formulaId: f.id })}>
                          {src ? <img src={src} alt="" loading="lazy" /> : <IconImage />}
                        </button>
                        <div style={{ minWidth: 0 }}>
                          <div className="MenusItemTop">
                            <input
                              className="Input"
                              value={String(f.title || '')}
                              onChange={(e) => patchFormula(f.id, { title: e.target.value })}
                              placeholder="Nom de la formule"
                            />
                            <input
                              className="Input MenusPriceInput"
                              value={String(f.price || '')}
                              onChange={(e) => patchFormula(f.id, { price: e.target.value })}
                              placeholder="Prix"
                            />
                          </div>
                          <input
                            className="Input"
                            value={String(f.subtitle || '')}
                            onChange={(e) => patchFormula(f.id, { subtitle: e.target.value })}
                            placeholder="Sous-titre (ex: proposition de la semaine)"
                          />
                          <textarea
                            className="Textarea"
                            rows={3}
                            value={String(f.description || '')}
                            onChange={(e) => patchFormula(f.id, { description: e.target.value })}
                            placeholder="Contenu de la formule"
                          />
                          <div className="Muted OneLine" style={{ marginTop: 6 }}>
                            {imageLabel(f) || 'Aucune image'}
                          </div>
                        </div>
                      </div>

                      <div className="MenusItemActions">
                        <label className="ButtonInline MenusToggleLine">
                          <input
                            type="checkbox"
                            checked={Boolean(f.is_available !== false)}
                            onChange={(e) => patchFormula(f.id, { is_available: Boolean(e.target.checked) })}
                          />
                          Active
                        </label>
                        <button type="button" className="Button Button--ghost Button--danger" onClick={() => requestRemoveFormula(f)}>
                          <IconTrash />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="MenusSaveBar">
            <button type="button" className="Button Button--ghost MenusSaveButton" disabled={!isDirty || saving || state.status === 'loading' || !state.ensureOk} onClick={saveMenu}>
              <span className="ButtonInline">{saving ? <Spinner className="Spinner--sm" /> : <IconCheck />} Enregistrer le menu</span>
            </button>
          </div>
        </>
      ) : null}

      {modal?.type === 'new-formula' ? (
        <Modal
          title="Nouvelle formule"
          icon={<IconPlus />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)} disabled={modal.saving}>
                Annuler
              </button>
              <button type="button" className="Button" onClick={createFormulaFromModal} disabled={modal.saving}>
                Créer
              </button>
            </>
          }
        >
          <div className="Field">
            <label className="Label">Nom</label>
            <input className="Input" value={String(modal.title || '')} onChange={(e) => setModal((m) => (m?.type === 'new-formula' ? { ...m, title: e.target.value } : m))} />
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Sous-titre</label>
            <input className="Input" value={String(modal.subtitle || '')} onChange={(e) => setModal((m) => (m?.type === 'new-formula' ? { ...m, subtitle: e.target.value } : m))} />
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Prix</label>
            <input className="Input" value={String(modal.price || '')} onChange={(e) => setModal((m) => (m?.type === 'new-formula' ? { ...m, price: e.target.value } : m))} />
          </div>
          <div className="Field" style={{ marginTop: 10 }}>
            <label className="Label">Description</label>
            <textarea className="Textarea" rows={4} value={String(modal.description || '')} onChange={(e) => setModal((m) => (m?.type === 'new-formula' ? { ...m, description: e.target.value } : m))} />
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'confirm-delete' ? (
        <Modal
          title="Confirmer la suppression"
          icon={<IconAlertTriangle />}
          onClose={() => setModal(null)}
          footer={
            <>
              <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
                Annuler
              </button>
              <button
                type="button"
                className="Button Button--danger"
                onClick={() => {
                  modal.onConfirm?.();
                  setModal(null);
                }}
              >
                Supprimer
              </button>
            </>
          }
        >
          <div className="Muted">
            Supprimer {modal.kind === 'category' ? 'la catégorie' : modal.kind === 'formula' ? 'la formule' : 'cet élément'} <strong>{String(modal.title || 'sans titre')}</strong> ?
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'image-picker' ? (
        <Modal
          title="Source image"
          icon={<IconImage />}
          onClose={() => setModal(null)}
          footer={
            <button type="button" className="Button Button--ghost" onClick={() => setModal(null)}>
              Fermer
            </button>
          }
        >
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = e.target.files ? Array.from(e.target.files) : [];
              e.target.value = '';
              if (!files.length) return;
              uploadFromPicker(files);
            }}
          />

          <div className="Badges" style={{ justifyContent: 'flex-start', marginBottom: 10 }}>
            <button
              type="button"
              className="Button Button--ghost"
              disabled={Boolean(modal.uploading)}
              onClick={() => uploadInputRef.current?.click?.()}
            >
              <span className="ButtonInline">
                <IconPlus /> {modal.uploading ? 'Upload…' : 'Uploader'}
              </span>
            </button>
          </div>

          <div className="Field">
            <label className="Label">URL image (optionnel)</label>
            <div className="InputRow">
              <input
                className="Input"
                value={String(modal.customUrl || '')}
                onChange={(e) => setModal((m) => (m?.type === 'image-picker' ? { ...m, customUrl: e.target.value } : m))}
                placeholder="https://..."
              />
              <button type="button" className="Button Button--ghost" onClick={applyUrlToPickerTarget}>
                Appliquer
              </button>
            </div>
          </div>

          {siteMedia.status === 'loading' ? <div className="Muted">Chargement des images…</div> : null}
          {modal.error ? (
            <div className="Error" role="alert" style={{ marginBottom: 10 }}>
              {modal.error}
            </div>
          ) : null}

          <div className="ExplorerGrid ExplorerGrid--files">
            {imageAssets.slice(0, 120).map((a) => {
              const src = mediaInlineUrl(apiBaseUrl, Number(siteId), Number(a.id));
              return (
                <button key={a.id} type="button" className="Tile GalleryTile" onClick={() => applyMediaToPickerTarget(a)}>
                  <div className="MediaThumbWrap">
                    <img src={src} alt="" className="MediaThumb" loading="lazy" />
                  </div>
                  <div className="OneLine" style={{ marginTop: 8 }}>
                    {String(a.original_name || a.note || `Media #${a.id}`)}
                  </div>
                </button>
              );
            })}
          </div>
        </Modal>
      ) : null}

      {modal?.type === 'db-status' ? (
        <MenusDbStatusModal
          siteId={Number(siteId)}
          dbConfig={state.dbConfig}
          ensureOk={state.ensureOk}
          ensureError={state.ensureOk ? '' : state.error}
          dbLoading={dbLoading}
          onEnsure={ensureTables}
          onClose={() => setModal(null)}
        />
      ) : null}

      {siteMedia.status === 'error' ? (
        <div className="Hint" style={{ marginTop: 10 }}>
          <span className="ButtonInline" style={{ gap: 8 }}>
            <IconAlertTriangle /> {siteMedia.error}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function MenusDbStatusModal({ siteId, dbConfig, ensureOk, ensureError, dbLoading = false, onEnsure, onClose }) {
  const [ensuring, setEnsuring] = useState(false);
  const [ensureLocalError, setEnsureLocalError] = useState('');
  const [test, setTest] = useState({ status: 'idle', error: '' }); // idle | loading | ok | error

  useEffect(() => {
    let cancelled = false;
    if (!dbConfig || !ensureOk) {
      setTest({ status: 'idle', error: '' });
      return () => {};
    }
    setTest({ status: 'loading', error: '' });
    api
      .menusDbTest({ site_id: Number(siteId), use_stored: true })
      .then((res) => {
        if (cancelled) return;
        if (!res.ok) {
          setTest({ status: 'error', error: res.error || 'Connexion impossible' });
          return;
        }
        setTest({ status: 'ok', error: '' });
      })
      .catch(() => {
        if (cancelled) return;
        setTest({ status: 'error', error: 'Connexion impossible' });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, dbConfig, ensureOk]);

  const step1 = dbLoading ? 'pending' : dbConfig ? 'ok' : 'bad';
  const step2 = step1 === 'ok' ? (ensureOk ? 'ok' : 'bad') : 'disabled';
  const step3 =
    step2 === 'ok'
      ? test.status === 'ok'
        ? 'ok'
        : test.status === 'error'
        ? 'bad'
        : test.status === 'loading'
        ? 'pending'
        : 'pending'
      : 'disabled';

  return (
    <Modal
      title="BDD Menus"
      icon={step2 === 'ok' && step3 === 'ok' ? <IconCheck /> : <IconAlertTriangle />}
      onClose={onClose}
      footer={
        <button type="button" className="Button Button--ghost" onClick={onClose}>
          Fermer
        </button>
      }
    >
      <DbSteps
        steps={[
          {
            key: 'db',
            title: 'Base de données',
            status: step1,
            detail: dbConfig ? `${dbConfig.host}:${dbConfig.port} · ${dbConfig.db_name}` : 'Aucune base configurée pour ce site.',
            actions: (
              <a className="Button Button--ghost" href="#/databases">
                <span className="ButtonInline">
                  <IconGlobe /> Ouvrir Bases de données
                </span>
              </a>
            ),
          },
          {
            key: 'tables',
            title: 'Tables Menus',
            status: step2,
            detail: step2 === 'ok' ? 'OK' : step2 === 'bad' ? ensureLocalError || ensureError || 'Tables manquantes' : '—',
            actions: dbConfig ? (
              <button
                type="button"
                className="Button"
                disabled={ensuring || dbLoading}
                onClick={async () => {
                  if (ensuring) return;
                  setEnsuring(true);
                  setEnsureLocalError('');
                  const res = await onEnsure?.();
                  if (!res?.ok) {
                    setEnsureLocalError(res?.error || 'Erreur');
                    setEnsuring(false);
                    return;
                  }
                  setEnsuring(false);
                }}
              >
                <span className="ButtonInline">
                  <IconPlus /> {ensuring ? 'Vérification…' : 'Initialiser / Vérifier'}
                </span>
              </button>
            ) : null,
          },
          {
            key: 'conn',
            title: 'Connexion',
            status: step3,
            detail:
              step3 === 'ok'
                ? 'Connecté'
                : step3 === 'bad'
                ? test.error || 'Connexion impossible'
                : step3 === 'pending'
                ? 'Test en cours…'
                : '—',
          },
        ]}
      />
    </Modal>
  );
}
